import { onMounted, onUnmounted } from 'vue'

// Tema do kanban segue o tema do Chatwoot quando rodando em iframe dentro
// dele (parent posta via postMessage). Quando standalone, segue
// prefers-color-scheme do OS.
//
// Aplica/remove a classe `dark` em document.documentElement; Tailwind dark
// mode com `darkMode: 'class'` ativa todos os utilitarios `dark:` quando
// presente.

type ResolvedTheme = 'light' | 'dark'

interface ThemeMessage {
  type: 'atomai:theme'
  value: ResolvedTheme
}

function applyTheme(theme: ResolvedTheme): void {
  const root = document.documentElement
  if (theme === 'dark') {
    root.classList.add('dark')
    root.style.colorScheme = 'dark'
  } else {
    root.classList.remove('dark')
    root.style.colorScheme = 'light'
  }
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

export function useTheme() {
  let mediaQuery: MediaQueryList | null = null
  let onMediaChange: ((e: MediaQueryListEvent) => void) | null = null

  function handleMessage(event: MessageEvent) {
    // Validacao basica de origem — so aceitamos do Chatwoot do cliente
    if (event.origin !== 'https://chat.pablomendes.atomai.digital') return
    const data = event.data as ThemeMessage | unknown
    if (
      typeof data === 'object' &&
      data !== null &&
      (data as ThemeMessage).type === 'atomai:theme'
    ) {
      const value = (data as ThemeMessage).value
      if (value === 'light' || value === 'dark') {
        applyTheme(value)
      }
    }
  }

  function startStandaloneWatch() {
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    applyTheme(mediaQuery.matches ? 'dark' : 'light')
    onMediaChange = (e: MediaQueryListEvent) =>
      applyTheme(e.matches ? 'dark' : 'light')
    mediaQuery.addEventListener('change', onMediaChange)
  }

  onMounted(() => {
    if (isInIframe()) {
      // Espera o parent enviar o tema; aplica light como default ate chegar.
      applyTheme('light')
      window.addEventListener('message', handleMessage)
      // Sinaliza ao parent que estamos prontos para receber theme
      try {
        window.parent.postMessage(
          { type: 'atomai:kanban-ready' },
          'https://chat.pablomendes.atomai.digital',
        )
      } catch {
        /* noop */
      }
    } else {
      startStandaloneWatch()
    }
  })

  onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
    if (mediaQuery && onMediaChange) {
      mediaQuery.removeEventListener('change', onMediaChange)
    }
  })
}
